import { useState, useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AccountNotVerified } from "@/components/AccountNotVerified";
import { createMolliePayment, SHOP_BETA, BETA_DISCOUNT_PERCENTAGE, PUBLIC_KEY_DISCOUNT_PERCENTAGE } from "@/services/mollieService";
import { checkPressKitExists } from "@/services/pressKitService";
import { toast } from "sonner";
import {
  Check,
  Star,
  Crown,
  Zap,
  TrendingUp,
  BarChart3,
  Rocket,
  Twitter,
  Mail,
  Lock,
  Sparkles,
  FileText,
  Users,
  Heart
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GAME_GENRES } from "@/data/gameGenres";

export default function Subscriptions() {
  const { user } = useAuth();
  const [userType, setUserType] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState<boolean | null>(null);
  const [isBetaUser, setIsBetaUser] = useState<boolean>(false);
  const [hasPublicKeyReduction, setHasPublicKeyReduction] = useState<boolean>(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(true);
  const [selectedGameLevel2, setSelectedGameLevel2] = useState<string | null>(null);
  const [selectedGameLevel3, setSelectedGameLevel3] = useState<string | null>(null);
  const [selectedGameLevel4, setSelectedGameLevel4] = useState<string | null>(null);
  const [selectedGameTwitter, setSelectedGameTwitter] = useState<string | null>(null);
  const [selectedGameNewsletter, setSelectedGameNewsletter] = useState<string | null>(null);
  const [selectedGamePressKit, setSelectedGamePressKit] = useState<string | null>(null);
  const [selectedGameCreatorPush, setSelectedGameCreatorPush] = useState<string | null>(null);
  const [selectedGenreCreatorPush, setSelectedGenreCreatorPush] = useState<string>("");
  const [games, setGames] = useState<any[]>([]);
  const [gamesWithPressKit, setGamesWithPressKit] = useState<Set<string>>(new Set());
  const [gamesHighlightInfo, setGamesHighlightInfo] = useState<Map<string, { highlight_id: number; highlight_end_date: string | null }>>(new Map());
  const [currentPlan, setCurrentPlan] = useState<string>("free");
  const [premiumBilling, setPremiumBilling] = useState<"monthly" | "yearly">("monthly");
  const [platformStats, setPlatformStats] = useState<any>(null);
  const [gameGenresMap, setGameGenresMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    fetchPlatformStats();
  }, []);

  const fetchPlatformStats = async () => {
    try {
      const { data: statsData } = await supabase
        .from('platform_stats')
        .select('data')
        .eq('category', 'genre_ranking')
        .single();
      setPlatformStats(statsData?.data || null);
    } catch (error) {
      console.error("Error fetching platform stats:", error);
    }
  };

  useEffect(() => {
    fetchUserType();
  }, [user?.id]);

  useEffect(() => {
    if (userType === 'developer' && user?.id) {
      checkVerificationStatus();
      fetchGames();
      checkPublicKeyReduction();
    } else {
      setIsCheckingVerification(false);
      setIsVerified(true);
    }
  }, [userType, user?.id]);

  const checkPublicKeyReduction = async () => {
    if (!user?.id) return;

    try {
      // Count public keys across all games for this developer
      // The RLS policy "Developers can view their game keys" ensures we only see keys for games owned by the user
      const { count, error } = await supabase
        .from("keys")
        .select("*", { count: "exact", head: true })
        .eq("public", true);

      if (error) {
        console.error("Error counting public keys:", error);
        return;
      }

      const hasReduction = (count || 0) > 10;
      setHasPublicKeyReduction(hasReduction);

      // Update the profile with the reduction status
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ public_key_reduction: hasReduction } as any)
        .eq("id", user.id);

      if (updateError) {
        console.error("Error updating public key reduction status:", updateError);
      }
    } catch (err) {
      console.error("Unexpected error in checkPublicKeyReduction:", err);
    }
  };

  const fetchUserType = async () => {
    if (user) {
      const { data, error } = await supabase
        .from("profiles")
        .select("user_type, is_premium, premium_end_date, is_beta, public_key_reduction")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error fetching user type:", error);
        return;
      }

      setUserType((data as any)?.user_type || null);
      setIsBetaUser((data as any)?.is_beta || false);
      setHasPublicKeyReduction((data as any)?.public_key_reduction || false);

      // Determine current plan based on is_premium and premium_end_date
      const isPremium = (data as any)?.is_premium;
      if (isPremium) {
        const endDate = (data as any)?.premium_end_date;
        if (!endDate || new Date(endDate) > new Date()) {
          setCurrentPlan("premium");
        } else {
          setCurrentPlan("free");
        }
      } else {
        setCurrentPlan("free");
      }
    }
  };

  const fetchGames = async () => {
    if (!user?.id) return;

    const { data } = await supabase
      .from("games")
      .select("id, title, highlight_id, highlight_end_date")
      .eq("developer_id", user.id)
      .order("created_at", { ascending: false });

    setGames(data || []);

    // Store highlight info for each game
    if (data && data.length > 0) {
      const highlightMap = new Map<string, { highlight_id: number; highlight_end_date: string | null }>();
      data.forEach((game: any) => {
        highlightMap.set(game.id, {
          highlight_id: game.highlight_id || 1,
          highlight_end_date: game.highlight_end_date || null
        });
      });
      setGamesHighlightInfo(highlightMap);

      // Check which games have press kits
      const pressKitChecks = await Promise.all(
        data.map(async (game: any) => {
          const hasPressKit = await checkPressKitExists(game.id);
          return { gameId: game.id, hasPressKit };
        })
      );

      const gamesWithPressKitSet = new Set<string>();
      pressKitChecks.forEach(({ gameId, hasPressKit }) => {
        if (hasPressKit) {
          gamesWithPressKitSet.add(gameId);
        }
      });

      setGamesWithPressKit(gamesWithPressKitSet);

      // Update default selected game if needed
      if (!selectedGameLevel2 && data && data.length > 0) {
        setSelectedGameLevel2((data[0] as any).id);
      }

      // Safe fetch for genres separately
      try {
        if (data && data.length > 0) {
          const gameIds = data.map((g: any) => g.id);
          const { data: genresData } = await supabase
            .from("games")
            .select("id, genre")
            .in("id", gameIds);

          if (genresData) {
            const genresMap = new Map<string, string>();
            genresData.forEach((item: any) => {
              if (item.genre) {
                genresMap.set(item.id, item.genre);
              }
            });
            setGameGenresMap(genresMap);
          }
        }
      } catch (error) {
        console.error("Error fetching genres (safe fallback):", error);
        // We continue without genres if this fails
      }
      if (!selectedGameLevel3) {
        setSelectedGameLevel3((data[0] as any).id);
      }
      if (!selectedGameLevel4) {
        setSelectedGameLevel4((data[0] as any).id);
      }
    }
  };

  // Check if a game is eligible for a level boost
  const isGameEligibleForLevelBoost = (gameId: string, targetLevel: number): boolean => {
    const highlightInfo = gamesHighlightInfo.get(gameId);
    if (!highlightInfo) return true; // If no info, assume eligible

    const { highlight_id, highlight_end_date } = highlightInfo;

    // Check if game already has a level equal or higher
    if (highlight_id >= targetLevel) {
      return false;
    }

    // Check if game has an active highlight period (end date in the future)
    if (highlight_end_date) {
      const endDate = new Date(highlight_end_date);
      const now = new Date();
      if (endDate > now) {
        return false; // Game has an active highlight period
      }
    }

    return true;
  };

  const checkVerificationStatus = async () => {
    if (!user?.id || userType !== 'developer') return;

    setIsCheckingVerification(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("is_verified")
        .eq("id", user.id)
        .single();

      if (error) {
        console.error("Error checking verification status:", error);
        setIsVerified(false);
      } else {
        setIsVerified((data as any)?.is_verified === true);
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
      setIsVerified(false);
    } finally {
      setIsCheckingVerification(false);
    }
  };

  const handleSubscribe = async (plan: string, billing: "monthly" | "yearly") => {
    if (!user?.id) {
      toast.error("You must be logged in to subscribe");
      return;
    }

    if (plan !== "premium") {
      toast.error("Only premium subscriptions are available");
      return;
    }

    try {
      const paymentType = billing === "monthly" ? "premium_monthly" : "premium_yearly";

      toast.loading("Creating payment...", { id: "payment-loading" });

      const result = await createMolliePayment(user.id, paymentType, isBetaUser, undefined, hasPublicKeyReduction);

      if (!result.success) {
        toast.error(result.error || "Failed to create payment", { id: "payment-loading" });
        return;
      }

      if (result.paymentUrl) {
        toast.success("Redirecting to payment...", { id: "payment-loading" });
        // Redirect to Mollie checkout
        window.location.href = result.paymentUrl;
      } else {
        toast.error("No payment URL received", { id: "payment-loading" });
      }
    } catch (error) {
      console.error("Error creating subscription:", error);
      toast.error("An error occurred while creating the payment", { id: "payment-loading" });
    }
  };

  const handlePurchaseUpsell = async (type: string, duration?: string) => {
    if (!user?.id) {
      toast.error("You must be logged in to purchase");
      return;
    }

    if (currentPlan !== "premium") {
      toast.error("Premium subscription is required to purchase promotional boosts");
      return;
    }

    // Map upsell types to payment types
    const paymentTypeMap: Record<string, string> = {
      level2: "level2_boost",
      level3: "level3_boost",
      level4: "level4_boost",
      twitter: "twitter_post",
      "newsletter-basic": "newsletter_basic",
      "press-kit": "press_kit",
      "creator-push": "creator_push",
    };

    const paymentType = paymentTypeMap[type];
    if (!paymentType) {
      toast.error("Invalid upsell type");
      return;
    }

    // Get metadata based on type
    const metadata: Record<string, any> = {};

    if (type === "level2") {
      if (!selectedGameLevel2) {
        toast.error("Please select a game");
        return;
      }
      metadata.game_id = selectedGameLevel2;
      metadata.duration = duration || "1week";
    } else if (type === "level3") {
      if (!selectedGameLevel3) {
        toast.error("Please select a game");
        return;
      }
      metadata.game_id = selectedGameLevel3;
      metadata.duration = duration || "1week";
    } else if (type === "level4") {
      if (!selectedGameLevel4) {
        toast.error("Please select a game");
        return;
      }
      metadata.game_id = selectedGameLevel4;
      metadata.duration = duration || "1week";
    } else if (type === "twitter") {
      if (!selectedGameTwitter) {
        toast.error("Please select a game");
        return;
      }
      metadata.game_id = selectedGameTwitter;
    } else if (type === "newsletter-basic") {
      if (!selectedGameNewsletter) {
        toast.error("Please select a game");
        return;
      }
      metadata.game_id = selectedGameNewsletter;
    } else if (type === "press-kit") {
      if (!selectedGamePressKit) {
        toast.error("Please select a game");
        return;
      }
      metadata.game_id = selectedGamePressKit;
    } else if (type === "creator-push") {
      if (!selectedGameCreatorPush) {
        toast.error("Please select a game");
        return;
      }
      if (!selectedGenreCreatorPush) {
        toast.error("Please select a genre");
        return;
      }
      metadata.game_id = selectedGameCreatorPush;
      metadata.genre = selectedGenreCreatorPush;
    }

    try {
      toast.loading("Creating payment...", { id: "upsell-loading" });

      const result = await createMolliePayment(user.id, paymentType, isBetaUser, metadata);

      if (!result.success) {
        toast.error(result.error || "Failed to create payment", { id: "upsell-loading" });
        return;
      }

      if (result.paymentUrl) {
        toast.success("Redirecting to payment...", { id: "upsell-loading" });
        // Redirect to Mollie checkout
        window.location.href = result.paymentUrl;
      } else {
        toast.error("No payment URL received", { id: "upsell-loading" });
      }
    } catch (error) {
      console.error("Error creating upsell payment:", error);
      toast.error("An error occurred while creating the payment", { id: "upsell-loading" });
    }
  };

  if (isCheckingVerification) {
    return (
      <Layout>
        <SEO
          title="Subscriptions"
          description="Choose the perfect plan for your studio and unlock premium features on IndieKeys."
        />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (userType === 'developer' && isVerified !== true) {
    return (
      <Layout>
        <SEO
          title="Subscriptions"
          description="Choose the perfect plan for your studio and unlock premium features on IndieKeys."
        />
        <AccountNotVerified />
      </Layout>
    );
  }

  const isPremium = currentPlan === "premium";
  const totalDiscountPercentage = (isBetaUser ? BETA_DISCOUNT_PERCENTAGE : 0) + (hasPublicKeyReduction ? PUBLIC_KEY_DISCOUNT_PERCENTAGE : 0);

  return (
    <Layout>
      <SEO
        title="Subscriptions"
        description="Choose the perfect plan for your studio and unlock premium features on IndieKeys."
      />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold mb-2">Choose Your Plan</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Select the perfect plan for your studio. Premium unlocks powerful promotional boosts to maximize your game's visibility.
          </p>
        </div>

        {/* Subscription Plans */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 max-w-5xl mx-auto">
          {/* Free Plan */}
          <Card className="relative">
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-lg flex items-center justify-center">
                <Star className="w-8 h-8 text-muted-foreground" />
              </div>
              <CardTitle className="text-2xl">Free</CardTitle>
              <CardDescription>Perfect for testing the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Add up to 15 creator + 15 public keys for your games</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="line-through">Access to games statistics</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="line-through">Access to premium boosts</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="line-through">Premium badge on profile</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="line-through">Access to events & campaigns</span>
                </div>
              </div>
              {currentPlan === "free" ? (
                <Button className="w-full" variant="outline" disabled>
                  Current Plan
                </Button>
              ) : (
                <Button className="w-full" variant="outline" onClick={() => handleSubscribe("free", "monthly")}>
                  Select Free
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Premium Plan */}
          <Card className="relative border-2 border-primary">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1">
                RECOMMENDED
              </Badge>
            </div>
            <CardHeader className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-lg flex items-center justify-center">
                <Crown className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">Premium Studio</CardTitle>
              <CardDescription>Unlock full potential + promotional boosts</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Unlimited keys addition for your games</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Access to games statistics</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Access to premium boosts</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Premium badge on profile</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>Access to events & campaigns</span>
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-3 mb-4">
                <div className="space-y-3">
                  <label
                    htmlFor="premium-monthly"
                    className="flex items-center space-x-3 p-3 rounded-lg border-2 border-muted/20 hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer group"
                  >
                    <div className="relative">
                      <input
                        type="radio"
                        id="premium-monthly"
                        name="premium-pricing"
                        value="monthly"
                        checked={premiumBilling === "monthly"}
                        onChange={() => setPremiumBilling("monthly")}
                        className="sr-only"
                      />
                      <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/40 group-hover:border-primary/60 transition-colors flex items-center justify-center">
                        <div className={`w-2 h-2 rounded-full bg-primary transition-opacity ${premiumBilling === "monthly" ? "opacity-100" : "opacity-0"}`}></div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium flex items-center flex-wrap gap-2">
                        {totalDiscountPercentage > 0 ? (
                          <>
                            <span className="line-through text-muted-foreground">€29.99</span>
                            <span>€{(29.99 * (1 - totalDiscountPercentage / 100)).toFixed(2)} / month</span>
                            <div className="flex gap-1 flex-wrap">
                              {isBetaUser && (
                                <Badge variant="secondary" className="bg-green-500/10 text-green-500 hover:bg-green-500/20 border-0 text-[10px] px-1.5 h-5">
                                  Beta -{BETA_DISCOUNT_PERCENTAGE}%
                                </Badge>
                              )}
                              {hasPublicKeyReduction && (
                                <Badge variant="secondary" className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 border-0 text-[10px] px-1.5 h-5">
                                  Public Keys -{PUBLIC_KEY_DISCOUNT_PERCENTAGE}%
                                </Badge>
                              )}
                            </div>
                          </>
                        ) : (
                          "€29.99 / month"
                        )}
                      </div>
                    </div>
                  </label>
                  <label
                    htmlFor="premium-yearly"
                    className="flex items-center space-x-3 p-3 rounded-lg border-2 border-muted/20 hover:border-primary/40 hover:bg-primary/5 transition-all cursor-pointer group"
                  >
                    <div className="relative">
                      <input
                        type="radio"
                        id="premium-yearly"
                        name="premium-pricing"
                        value="yearly"
                        checked={premiumBilling === "yearly"}
                        onChange={() => setPremiumBilling("yearly")}
                        className="sr-only"
                      />
                      <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/40 group-hover:border-primary/60 transition-colors flex items-center justify-center">
                        <div className={`w-2 h-2 rounded-full bg-primary transition-opacity ${premiumBilling === "yearly" ? "opacity-100" : "opacity-0"}`}></div>
                      </div>
                    </div>
                    <div className="flex-1 flex items-center justify-between">
                      <div className="text-sm font-medium flex items-center flex-wrap gap-2">
                        {totalDiscountPercentage > 0 ? (
                          <>
                            <span className="line-through text-muted-foreground">€299.99</span>
                            <span>€{(299.99 * (1 - totalDiscountPercentage / 100)).toFixed(2)} / year</span>
                            <div className="flex gap-1 flex-wrap">
                              {isBetaUser && (
                                <Badge variant="secondary" className="bg-green-500/10 text-green-500 hover:bg-green-500/20 border-0 text-[10px] px-1.5 h-5">
                                  Beta -{BETA_DISCOUNT_PERCENTAGE}%
                                </Badge>
                              )}
                              {hasPublicKeyReduction && (
                                <Badge variant="secondary" className="bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 border-0 text-[10px] px-1.5 h-5">
                                  Public Keys -{PUBLIC_KEY_DISCOUNT_PERCENTAGE}%
                                </Badge>
                              )}
                            </div>
                          </>
                        ) : (
                          "€299.99 / year"
                        )}
                      </div>
                      {!totalDiscountPercentage && <Badge variant="secondary" className="text-xs">17% off</Badge>}
                    </div>
                  </label>
                </div>
              </div>
              {currentPlan === "premium" ? (
                <Button className="w-full" disabled>
                  <Crown className="w-4 h-4 mr-2" />
                  Current Plan
                </Button>
              ) : (
                <Button className="w-full" onClick={() => handleSubscribe("premium", premiumBilling)}>
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Premium
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Premium Upsells Section */}
        <div className="mb-12">
          <div className="mb-6 text-center">
            <h2 className="text-3xl font-bold mb-2">Premium Promotional Boosts</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {isPremium ? (
                "Unlock powerful promotional tools to boost your game's visibility during launches, updates, or special events."
              ) : (
                <>
                  <Lock className="w-4 h-4 inline mr-1" />
                  Premium required to unlock promotional boosts
                </>
              )}
            </p>
          </div>

          {!isPremium && (
            <Card className="mb-8 bg-muted/50 border-2 border-dashed">
              <CardContent className="py-8 text-center">
                <Lock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">Premium Required</h3>
                <p className="text-muted-foreground mb-4">
                  Upgrade to Premium to access all promotional boosts and maximize your game's visibility.
                </p>
                <Button onClick={() => handleSubscribe("premium", "monthly")}>
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade to Premium
                </Button>
              </CardContent>
            </Card>
          )}


          {/* Upsell Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Level 2 Boost */}
            <Card className={!isPremium ? "opacity-60" : ""}>
              <CardHeader>
                <div className="w-12 h-12 mx-auto mb-3 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-center">Level 2 Boost</CardTitle>
                <CardDescription className="text-center">More Visibility</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold">€9.99</div>
                  <div className="text-sm text-muted-foreground">/ 1 week for one game</div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Creator homepage minimum highlights</span>
                  </div>
                </div>
                {isPremium && games.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium mb-1 block">Select a game:</label>
                    <Select
                      value={selectedGameLevel2 || ""}
                      onValueChange={(value) => setSelectedGameLevel2(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a game..." />
                      </SelectTrigger>
                      <SelectContent>
                        {games.map((game: any) => {
                          const isEligible = isGameEligibleForLevelBoost(game.id, 2);
                          return (
                            <SelectItem
                              key={game.id}
                              value={game.id}
                              disabled={!isEligible}
                              className={!isEligible ? "opacity-50" : ""}
                            >
                              {game.title}{!isEligible ? " (Not eligible)" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedGameLevel2 && !isGameEligibleForLevelBoost(selectedGameLevel2, 2) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        This game already has a level 2+ boost or an active highlight period.
                      </p>
                    )}
                  </div>
                )}
                <Button
                  className="w-full"
                  variant={isPremium ? "default" : "outline"}
                  disabled={!isPremium || !selectedGameLevel2 || (selectedGameLevel2 && !isGameEligibleForLevelBoost(selectedGameLevel2, 2))}
                  onClick={() => handlePurchaseUpsell("level2", "1week")}
                >
                  {isPremium ? "Purchase" : <Lock className="w-4 h-4" />}
                </Button>
              </CardContent>
            </Card>

            {/* Level 3 Boost */}
            <Card className={!isPremium ? "opacity-60" : ""}>
              <CardHeader>
                <div className="w-12 h-12 mx-auto mb-3 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Rocket className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-center">Level 3 Boost</CardTitle>
                <CardDescription className="text-center">Enhanced Visibility</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold">€19.99</div>
                  <div className="text-sm text-muted-foreground">/ 1 week for one game</div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Creator homepage</span>
                  </div>
                </div>
                {isPremium && games.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium mb-1 block">Select a game:</label>
                    <Select
                      value={selectedGameLevel3 || ""}
                      onValueChange={(value) => setSelectedGameLevel3(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a game..." />
                      </SelectTrigger>
                      <SelectContent>
                        {games.map((game: any) => {
                          const isEligible = isGameEligibleForLevelBoost(game.id, 3);
                          return (
                            <SelectItem
                              key={game.id}
                              value={game.id}
                              disabled={!isEligible}
                              className={!isEligible ? "opacity-50" : ""}
                            >
                              {game.title}{!isEligible ? " (Not eligible)" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedGameLevel3 && !isGameEligibleForLevelBoost(selectedGameLevel3, 3) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        This game already has a level 3+ boost or an active highlight period.
                      </p>
                    )}
                  </div>
                )}
                <Button
                  className="w-full"
                  variant={isPremium ? "default" : "outline"}
                  disabled={!isPremium || !selectedGameLevel3 || (selectedGameLevel3 && !isGameEligibleForLevelBoost(selectedGameLevel3, 3))}
                  onClick={() => handlePurchaseUpsell("level3", "1week")}
                >
                  {isPremium ? "Purchase" : <Lock className="w-4 h-4" />}
                </Button>
              </CardContent>
            </Card>

            {/* Level 4 Boost */}
            <Card className={!isPremium ? "opacity-60" : ""}>
              <CardHeader>
                <div className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-purple-500" />
                </div>
                <CardTitle className="text-lg text-center">Level 4 Boost</CardTitle>
                <CardDescription className="text-center">Maximum Visibility</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold">€29.99</div>
                  <div className="text-sm text-muted-foreground">/ 1 week for one game</div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Hero section + top carousel</span>
                  </div>
                </div>
                {isPremium && games.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium mb-1 block">Select a game:</label>
                    <Select
                      value={selectedGameLevel4 || ""}
                      onValueChange={(value) => setSelectedGameLevel4(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a game..." />
                      </SelectTrigger>
                      <SelectContent>
                        {games.map((game: any) => {
                          const isEligible = isGameEligibleForLevelBoost(game.id, 4);
                          return (
                            <SelectItem
                              key={game.id}
                              value={game.id}
                              disabled={!isEligible}
                              className={!isEligible ? "opacity-50" : ""}
                            >
                              {game.title}{!isEligible ? " (Not eligible)" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedGameLevel4 && !isGameEligibleForLevelBoost(selectedGameLevel4, 4) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        This game already has a level 4 boost or an active highlight period.
                      </p>
                    )}
                  </div>
                )}
                <Button
                  className="w-full"
                  variant={isPremium ? "default" : "outline"}
                  disabled={!isPremium || !selectedGameLevel4 || (selectedGameLevel4 && !isGameEligibleForLevelBoost(selectedGameLevel4, 4))}
                  onClick={() => handlePurchaseUpsell("level4", "1week")}
                >
                  {isPremium ? "Purchase" : <Lock className="w-4 h-4" />}
                </Button>
              </CardContent>
            </Card>



            {/* Press Kit Auto-Builder */}
            <Card className={!isPremium ? "opacity-60" : ""}>
              <CardHeader>
                <div className="w-12 h-12 mx-auto mb-3 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-green-500" />
                </div>
                <CardTitle className="text-lg text-center">Press Kit Auto-Builder</CardTitle>
                <CardDescription className="text-center">All-in-one Professional Press Kit</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold">€14.99</div>
                  <div className="text-sm text-muted-foreground">one-time</div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Auto-generated press kit</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Industry's best practices</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Easy-to-use</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Effective press kit</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>.zip Export</span>
                  </div>
                </div>
                {isPremium && games.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium mb-1 block">Select a game:</label>
                    <Select
                      value={selectedGamePressKit || ""}
                      onValueChange={(value) => setSelectedGamePressKit(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a game..." />
                      </SelectTrigger>
                      <SelectContent>
                        {games.map(game => {
                          const hasPressKit = gamesWithPressKit.has(game.id);
                          return (
                            <SelectItem
                              key={game.id}
                              value={game.id}
                              disabled={hasPressKit}
                              className={hasPressKit ? "opacity-50" : ""}
                            >
                              {game.title}{hasPressKit ? " (Press kit already exists)" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    {selectedGamePressKit && gamesWithPressKit.has(selectedGamePressKit) && (
                      <p className="text-xs text-muted-foreground mt-1">
                        This game already has a press kit.
                      </p>
                    )}
                  </div>
                )}
                <Button
                  className="w-full"
                  variant={isPremium ? "default" : "outline"}
                  disabled={!isPremium || !selectedGamePressKit || (selectedGamePressKit && gamesWithPressKit.has(selectedGamePressKit))}
                  onClick={() => handlePurchaseUpsell("press-kit")}
                >
                  {isPremium ? "Purchase" : <Lock className="w-4 h-4" />}
                </Button>
              </CardContent>
            </Card>

            {/* Twitter Post */}
            <Card className={!isPremium ? "opacity-60" : ""}>
              <CardHeader>
                <div className="w-12 h-12 mx-auto mb-3 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Twitter className="w-6 h-6 text-blue-500" />
                </div>
                <CardTitle className="text-lg text-center">X/Twitter Post</CardTitle>
                <CardDescription className="text-center">Social Promotion</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold">€14.99</div>
                  <div className="text-sm text-muted-foreground">one-time</div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Featured on official account</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Reach our creator network</span>
                  </div>
                </div>
                {isPremium && games.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium mb-1 block">Select a game:</label>
                    <Select
                      value={selectedGameTwitter || ""}
                      onValueChange={(value) => setSelectedGameTwitter(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a game..." />
                      </SelectTrigger>
                      <SelectContent>
                        {games.map(game => (
                          <SelectItem key={game.id} value={game.id}>{game.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* <Button 
                  className="w-full" 
                  variant={isPremium ? "default" : "outline"}
                  disabled={!isPremium || !selectedGameTwitter}
                  onClick={() => handlePurchaseUpsell("twitter")}
                >
                  {isPremium ? "Purchase" : <Lock className="w-4 h-4" />}
                </Button> */}
                <Button
                  className="w-full bg-border"
                  variant={isPremium ? "default" : "outline"}
                  disabled={true}
                >
                  <Lock className="w-4 h-4" /> Coming soon
                </Button>
              </CardContent>
            </Card>

            {/* Newsletter Basic */}
            <Card className={!isPremium ? "opacity-60" : ""}>
              <CardHeader>
                <div className="w-12 h-12 mx-auto mb-3 bg-orange-500/10 rounded-lg flex items-center justify-center">
                  <Mail className="w-6 h-6 text-orange-500" />
                </div>
                <CardTitle className="text-lg text-center">Newsletter Basic</CardTitle>
                <CardDescription className="text-center">Email Campaign</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold">€19.99</div>
                  <div className="text-sm text-muted-foreground">one-time</div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Featured in weekly newsletter</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>1 game per newsletter</span>
                  </div>
                </div>
                {isPremium && games.length > 0 && (
                  <div className="mb-4">
                    <label className="text-xs font-medium mb-1 block">Select a game:</label>
                    <Select
                      value={selectedGameNewsletter || ""}
                      onValueChange={(value) => setSelectedGameNewsletter(value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a game..." />
                      </SelectTrigger>
                      <SelectContent>
                        {games.map(game => (
                          <SelectItem key={game.id} value={game.id}>{game.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* <Button 
                  className="w-full" 
                  variant={isPremium ? "default" : "outline"}
                  disabled={!isPremium || !selectedGameNewsletter}
                  onClick={() => handlePurchaseUpsell("newsletter-basic")}
                >
                  {isPremium ? "Purchase" : <Lock className="w-4 h-4" />}
                </Button> */}
                <Button
                  className="w-full bg-border"
                  variant={isPremium ? "default" : "outline"}
                  disabled={true}
                >
                  <Lock className="w-4 h-4" /> Coming soon
                </Button>
              </CardContent>
            </Card>

            {/* Creator Push */}
            <Card className={!isPremium ? "opacity-60" : ""}>
              <CardHeader>
                <div className="w-12 h-12 mx-auto mb-3 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <CardTitle className="text-lg text-center">Creator Push</CardTitle>
                <CardDescription className="text-center">Targeted Recommendations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold">€19.99</div>
                  <div className="text-sm text-muted-foreground">one-time</div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Auto-recommended to matching creators</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>Genre-based targeting</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>1 push per game</span>
                  </div>
                </div>
                {isPremium && games.length > 0 && (
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="text-xs font-medium mb-1 block">Select a game:</label>
                      <Select
                        value={selectedGameCreatorPush || ""}
                        onValueChange={(value) => {
                          setSelectedGameCreatorPush(value);
                          setSelectedGenreCreatorPush(""); // Reset genre when game changes
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Choose a game..." />
                        </SelectTrigger>
                        <SelectContent>
                          {games.map(game => (
                            <SelectItem key={game.id} value={game.id}>{game.title}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">Select a genre/tag:</label>
                      <Select
                        value={selectedGenreCreatorPush}
                        onValueChange={(value) => setSelectedGenreCreatorPush(value)}
                        disabled={!selectedGameCreatorPush}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Choose a genre..." />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            // If we have genres for this game, filter. Otherwise show all (safe fallback)
                            let availableGenres = GAME_GENRES;

                            if (selectedGameCreatorPush) {
                              const gameGenresStr = gameGenresMap.get(selectedGameCreatorPush);
                              if (gameGenresStr) {
                                availableGenres = gameGenresStr.split(',').map(g => g.trim());
                              }
                            }

                            return availableGenres.map(genre => (
                              <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                      {selectedGenreCreatorPush && platformStats && (
                        <div className="mt-2 text-xs text-muted-foreground bg-muted p-2 rounded-md">
                          {(() => {
                            const creatorStats = platformStats?.creator?.find((g: any) => g.name === selectedGenreCreatorPush);
                            const playerStats = platformStats?.player?.find((g: any) => g.name === selectedGenreCreatorPush);
                            return (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-1.5">
                                  <Users className="w-3 h-3 text-primary" />
                                  <span>
                                    <span className="font-medium text-foreground">{creatorStats?.count || 0}</span> creators interested
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Heart className="w-3 h-3 text-rose-500" />
                                  <span>
                                    <span className="font-medium text-foreground">{playerStats?.count || 0}</span> players interested
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* <Button 
                  className="w-full" 
                  variant={isPremium ? "default" : "outline"}
                  disabled={!isPremium || !selectedGameCreatorPush || !selectedGenreCreatorPush}
                  onClick={() => handlePurchaseUpsell("creator-push")}
                >
                  {isPremium ? "Purchase" : <Lock className="w-4 h-4" />}
                </Button> */}
                <Button
                  className="w-full bg-border"
                  variant={isPremium ? "default" : "outline"}
                  disabled={true}
                >
                  <Lock className="w-4 h-4" /> Coming soon
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Info Section */}
        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
          <CardContent className="py-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Why Premium?</h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Premium gives you the foundation to grow. Promotional boosts help you maximize visibility during launches,
              updates, sales, or special events. Perfect for studios that want to make an impact.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mt-8">
              <div className="text-center">
                <Zap className="w-8 h-8 mx-auto mb-2 text-primary" />
                <h4 className="font-semibold mb-1">Boost Launches</h4>
                <p className="text-sm text-muted-foreground">Maximize visibility during game launches</p>
              </div>
              <div className="text-center">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 text-primary" />
                <h4 className="font-semibold mb-1">Revive Interest</h4>
                <p className="text-sm text-muted-foreground">Re-energize visibility for existing games</p>
              </div>
              <div className="text-center">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 text-primary" />
                <h4 className="font-semibold mb-1">Track Performance</h4>
                <p className="text-sm text-muted-foreground">Advanced analytics to optimize your strategy</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
